// AdminBattlesPanel — Super Admin master toggle for the Silo Wars
// add-on. When OFF, every battle UI element across the platform hides
// itself (BattleInboxBadge, BattleInboxCard, dashboard entry, fan portal
// link + section, /wars routes still resolve but server endpoints
// return 503). Default OFF — feature is launched on Randy's say-so.
import { useEffect, useState } from "react";
import { Sword, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { invalidateBattlesEnabled } from "@/features/battles/useBattlesEnabled";

export default function AdminBattlesPanel() {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/platform-flags");
      setEnabled(!!r.data?.battles_enabled);
    } catch (e) {
      toast.error("Failed to load battles toggle.");
      setEnabled(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.patch("/admin/platform-settings", { battles_enabled: next });
      setEnabled(next);
      invalidateBattlesEnabled();
      toast.success(next ? "Silo Wars is now visible across the platform." : "Silo Wars hidden across the platform.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (enabled === null) return <div className="text-sm text-[#6B6A68]">Loading…</div>;

  return (
    <div className="bg-white border hairline rounded-lg p-6" data-testid="admin-battles-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sword size={18} weight="duotone" className="text-[#C16E5A]" />
          <h3 className="font-serif-display text-xl">Silo Wars · master toggle</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#A89360]">Add-on</span>
      </div>

      <div className="flex items-start gap-4 mt-4">
        <button
          type="button"
          onClick={() => save(!enabled)}
          disabled={saving}
          aria-pressed={enabled}
          data-testid="admin-battles-toggle"
          className={`relative w-12 h-7 rounded-full transition flex-shrink-0 mt-1 ${enabled ? "bg-[#C16E5A]" : "bg-[#D8D6D2]"} ${saving ? "opacity-50" : ""}`}
        >
          <span
            className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all"
            style={{ left: enabled ? "1.5rem" : "0.125rem" }}
          />
        </button>
        <div className="flex-1">
          <div className="text-base font-medium text-[#1C1B1A] mb-1 flex items-center gap-2">
            {enabled ? (
              <><CheckCircle size={16} weight="fill" className="text-[#4A5D4E]" /> Battles enabled</>
            ) : (
              <><Warning size={16} weight="fill" className="text-[#A89360]" /> Battles disabled</>
            )}
          </div>
          <p className="text-sm text-[#6B6A68] leading-relaxed">
            {enabled
              ? "Creator Pro members can issue battles. All members can spectate. Battle entry points appear on the Dashboard, Fan Portal, and Identity Pill."
              : "Every battle UI element hides itself across the entire platform — Dashboard buttons, Fan Portal section, the ⚔ inbox badge, the /wars routes themselves all act as if the feature doesn't exist. Toggle on to launch."}
          </p>
          <ul className="text-[11px] text-[#6B6A68] mt-3 space-y-0.5 list-disc list-inside">
            <li>Initiating a battle: Creator Pro members + Super Admin only</li>
            <li>Watching battles: any member (Fan Portal section + /wars)</li>
            <li>Same-owner & self-vs-self: all LLMs unlocked, no Elo impact</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
